import React, { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, MessageSquare, Clock, CheckCircle, Loader2, Building2, RefreshCw } from "lucide-react";
import { useSearchParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const subjects = [
  "Sales Inquiry",
  "Technical Support",
  "Billing & Payments",
  "Partnership",
  "Other",
];

function generateCaptcha() {
  const a = Math.floor(Math.random() * 20) + 1;
  const b = Math.floor(Math.random() * 20) + 1;
  return { question: `${a} + ${b}`, answer: a + b };
}

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ 
    name: "", email: "", phone: "", 
    subject: searchParams.get("subject") || "", 
    message: searchParams.get("message") || "" 
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captcha, setCaptcha] = useState(generateCaptcha);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState(false);

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    setCaptchaInput("");
    setCaptchaError(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseInt(captchaInput) !== captcha.answer) {
      setCaptchaError(true);
      toast.error("Incorrect captcha answer. Please try again.");
      refreshCaptcha();
      return;
    }
    setCaptchaError(false);
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setCaptchaInput("");
      refreshCaptcha();
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: "Email Us", value: "support@indiacloud.ai", color: "#00F3FF" },
    { icon: Phone, label: "Call Us", value: "+011 41103555 / +91 9873385555", color: "#BC13FE" },
    { icon: MapPin, label: "Visit Us", value: "B-19, Mohan Estate, New Delhi-110044", color: "#00FF94" },
    { icon: Building2, label: "Registered As", value: "MIEUX TECHNOLOGIES PVT LTD", color: "#ffcc00" },
    { icon: Clock, label: "Support Hours", value: "Human Support 24x7", color: "#00F3FF" },
  ];

  return (
    <div data-testid="contact-page" className="page-enter pt-24">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden"
        style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0,243,255,0.08) 0%, transparent 60%), #030305" }}>
        <div className="animated-grid absolute inset-0 opacity-25" />
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-5 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
            style={{ background: "rgba(0,243,255,0.08)", border: "1px solid rgba(0,243,255,0.25)", color: "#00F3FF" }}>
            <MessageSquare size={12} /> GET IN TOUCH
          </div>
          <h1 className="font-orbitron font-900 text-4xl lg:text-5xl text-white mb-4">
            We're Here to <span className="text-gradient">Help</span>
          </h1>
          <p className="font-outfit text-base text-gray-400" style={{ fontWeight: 300 }}>
            Have a question? Talk to sales, get technical support, or just say hello.
            We respond within 2 minutes via AI, and 4 hours via our human team.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            {submitted ? (
              <div className="glass rounded-2xl p-10 flex flex-col items-center justify-center text-center h-full"
                style={{ minHeight: "420px" }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                  style={{ background: "rgba(0,255,148,0.1)", border: "1px solid rgba(0,255,148,0.3)" }}>
                  <CheckCircle size={32} color="#00FF94" />
                </div>
                <h2 className="font-orbitron font-700 text-xl text-white mb-3">Message Sent!</h2>
                <p className="font-outfit text-sm text-gray-400 mb-6" style={{ fontWeight: 300 }}>
                  Thanks for reaching out! Our team will respond within 24 hours. Meanwhile,
                  CloudBot is available 24/7 in the chat bubble.
                </p>
                <button data-testid="contact-send-another" onClick={() => setSubmitted(false)} className="btn-neon px-6 py-2.5 text-sm">
                  Send Another Message
                </button>
              </div>
            ) : (
              <div className="glass rounded-2xl p-8">
                <h2 className="font-rajdhani font-700 text-xl text-white mb-6 tracking-wide">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">FULL NAME *</label>
                      <input data-testid="contact-name" required value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Your full name" className="neon-input" />
                    </div>
                    <div>
                      <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">EMAIL *</label>
                      <input data-testid="contact-email" required type="email" value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="your@email.com" className="neon-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">PHONE</label>
                      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+91 9999999999" className="neon-input" />
                    </div>
                    <div>
                      <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">SUBJECT</label>
                      <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        className="neon-input">
                        <option value="" style={{ background: "#0A0A12" }}>Select subject</option>
                        {subjects.map((s) => <option key={s} value={s} style={{ background: "#0A0A12" }}>{s}</option>)}
                        {/* Ensure a custom subject matches if passed in URL */}
                        {form.subject && !subjects.includes(form.subject) && (
                          <option value={form.subject} style={{ background: "#0A0A12" }}>{form.subject}</option>
                        )}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">MESSAGE *</label>
                    <textarea data-testid="contact-message" required value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Tell us how we can help you..."
                      rows={4} className="neon-input resize-none" />
                  </div>

                  {/* Math Captcha */}
                  <div className="rounded-xl p-4" style={{ background: "rgba(0,243,255,0.04)", border: "1px solid rgba(0,243,255,0.12)" }}>
                    <div className="flex items-center justify-between mb-2">
                      <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider">VERIFY YOU'RE HUMAN *</label>
                      <button type="button" onClick={refreshCaptcha} className="text-gray-500 hover:text-white transition-colors" title="New captcha">
                        <RefreshCw size={14} />
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="font-orbitron font-700 text-lg px-4 py-2 rounded-lg"
                        data-testid="captcha-question"
                        style={{ background: "rgba(0,243,255,0.08)", border: "1px solid rgba(0,243,255,0.2)", color: "#00F3FF", whiteSpace: "nowrap" }}>
                        {captcha.question} = ?
                      </div>
                      <input
                        data-testid="captcha-input"
                        required
                        type="number"
                        value={captchaInput}
                        onChange={(e) => { setCaptchaInput(e.target.value); setCaptchaError(false); }}
                        placeholder="Answer"
                        className="neon-input"
                        style={{ maxWidth: "120px", borderColor: captchaError ? "rgba(255,50,50,0.6)" : undefined }}
                      />
                    </div>
                    {captchaError && (
                      <p className="text-xs mt-2 font-outfit" style={{ color: "#ff5050" }}>Incorrect answer. Try the new question.</p>
                    )}
                  </div>

                  <button data-testid="contact-submit" type="submit" disabled={loading}
                    className="btn-primary flex items-center justify-center gap-2 py-3 text-sm font-700 mt-2">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    {loading ? "SENDING..." : "SEND MESSAGE"}
                  </button>
                </form>
              </div>
            )}
          </div>

          {/* Contact Info */}
          <div className="flex flex-col gap-5">
            {contactInfo.map((item) => (
              <div key={item.label} className="glass rounded-2xl p-5 flex items-start gap-4 hover:-translate-y-0.5 transition-all duration-300">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${item.color}15`, border: `1px solid ${item.color}25` }}>
                  <item.icon size={18} color={item.color} />
                </div>
                <div>
                  <div className="font-rajdhani font-700 text-sm text-white mb-0.5">{item.label}</div>
                  <div className="font-outfit text-sm text-gray-400" style={{ fontWeight: 300 }}>{item.value}</div>
                </div>
              </div>
            ))}

            {/* Response time promise */}
            <div className="rounded-2xl p-6 mt-2"
              style={{ background: "linear-gradient(135deg, rgba(0,243,255,0.06), rgba(188,19,254,0.06))", border: "1px solid rgba(0,243,255,0.15)" }}>
              <h3 className="font-rajdhani font-700 text-base text-white mb-3">Our Response Promise</h3>
              <div className="flex flex-col gap-2">
                {[
                  { time: "2 min", label: "AI Support (CloudBot)", color: "#00FF94" },
                  { time: "4 hrs", label: "Human Support 24x7", color: "#00F3FF" },
                  { time: "24 hrs", label: "Complex technical issues", color: "#BC13FE" },
                ].map((r) => (
                  <div key={r.label} className="flex items-center gap-3">
                    <span className="font-orbitron font-700 text-sm w-12" style={{ color: r.color }}>{r.time}</span>
                    <span className="font-outfit text-xs text-gray-500">{r.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
